import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_INTERVENTIONTYPES } from "../../../utils/constants";
import interventionTypeService from "../../../services/ticketService/interventionTypeService";
import { InterventionTypeInterface } from "../../../models/TicketModule/InterventionType";

interface DeleteInterventionTypeContext {
  previousInterventionType: InterventionTypeInterface[];
  deletedInterventionType: InterventionTypeInterface | undefined;
}

const useDeleteInterventionType = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the interventionType to delete
    DeleteInterventionTypeContext
  >({
    mutationFn: interventionTypeService.Remove, // Remove should take a string ID

    onMutate: (interventionTypeId: string) => {
      const previousInterventionType =
        queryClient.getQueryData<InterventionTypeInterface[]>([
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<InterventionTypeInterface[]>(
        [
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousInterventionType.filter(
          (interventionType) => interventionType.id !== interventionTypeId
        ) // Use interventionTypeId to filter
      );

      return {
        previousInterventionType,
        deletedInterventionType: previousInterventionType.find(
          (interventionType) => interventionType.id === interventionTypeId
        ),
      };
    },

    onError: (error, interventionTypeId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<InterventionTypeInterface[]>(
        [
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousInterventionType
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteInterventionType;
